import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { Input } from 'reactstrap';
import { noop } from 'lodash';
import RadarChart from 'js/components/Charts/RadarChart/RadarChart';
import { Icon } from 'js/components';
import CategoryPills from 'js/components/Charts/CategoryLayout/CategoryPills';
import SeriesRadarLegend from './SeriesRadarLegend';
import {
  getAvailableRadarDepths,
  getDefaultRadarDepth,
  radarDepthLabel,
  buildRadarAxes,
  scoresForRadarAxes,
  rangesForRadarAxes,
  applySeriesPalette,
  seriesPaletteClasses,
  defaultSeriesFilterKeys,
  reconcileSeriesFilterKeys,
  DEFAULT_SERIES_PALETTE,
} from './series-radar-utils';
import styles from './SeriesRadarChart.module.scss';

/**
 * Polygon overlay of several scored series on shared axes.
 *
 * `questionSets` is the axis skeleton (which categories/questions to draw).
 * `series[].questionSets` are the scores for each series. When a lens has no
 * scores, pass the overall questionSets so the grid still draws.
 */
function SeriesRadarChart({
  questionSets = {},
  series: seriesItems = [],
  categoryLookups,
  categoryColorMap,
  legendHeading = 'Series',
  legendCaption = null,
  seriesPalette = DEFAULT_SERIES_PALETTE,
  emptyMessage = 'No responses',
  emptyComponent: EmptyComponent,
  isLoading = false,
  resetPinKey,
  pinnedKey,
  activeKey,
  onPin = noop,
  onClearPin = noop,
  onPeek = noop,
  onClearPeek = noop,
  hideLegend = false,
  compact = false,
  mode = 'dark',
  enableSeriesFilter = false,
  maxSeries = 0,
  dimInactive = false,
}) {
  const [internalPinned, setInternalPinned] = useState(null);
  const [internalPeeked, setInternalPeeked] = useState(null);

  useEffect(() => {
    setInternalPinned(null);
    setInternalPeeked(null);
  }, [resetPinKey]);

  const [selectedKeys, setSelectedKeys] = useState(() =>
    defaultSeriesFilterKeys(seriesItems, maxSeries)
  );
  const seriesKeySignature = seriesItems.map((item) => item.key).join('\0');

  useEffect(() => {
    setSelectedKeys((current) => {
      const next = reconcileSeriesFilterKeys(current, seriesItems, maxSeries);
      if (
        next.length === current.length &&
        next.every((key, index) => key === current[index])
      ) {
        return current;
      }
      return next;
    });
  }, [maxSeries, seriesItems, seriesKeySignature]);

  const visibleSeriesItems = useMemo(() => {
    if (!enableSeriesFilter || seriesItems.length <= 1) {
      return seriesItems;
    }
    const selected = new Set(selectedKeys);
    return seriesItems.filter((item) => selected.has(item.key));
  }, [enableSeriesFilter, seriesItems, selectedKeys]);

  const palettedItems = useMemo(
    () => applySeriesPalette(seriesItems, seriesPalette),
    [seriesItems, seriesPalette]
  );
  const visiblePalettedItems = useMemo(
    () => applySeriesPalette(visibleSeriesItems, seriesPalette),
    [visibleSeriesItems, seriesPalette]
  );

  const resolvedPinned = pinnedKey !== undefined ? pinnedKey : internalPinned;
  const resolvedActive =
    activeKey !== undefined ? activeKey : internalPeeked ?? internalPinned;

  const availableDepths = useMemo(
    () => getAvailableRadarDepths(questionSets),
    [questionSets]
  );

  const [depth, setDepth] = useState(() =>
    getDefaultRadarDepth(availableDepths)
  );
  const peekTimeout = useRef(null);

  useEffect(() => {
    if (!availableDepths.includes(depth)) {
      setDepth(getDefaultRadarDepth(availableDepths));
    }
  }, [availableDepths, depth]);

  useEffect(
    () => () => {
      clearTimeout(peekTimeout.current);
    },
    []
  );

  const axes = useMemo(
    () =>
      buildRadarAxes(questionSets, depth, {
        categoryLookups,
        categoryColorMap,
      }),
    [questionSets, depth, categoryLookups, categoryColorMap]
  );

  const categories = useMemo(
    () => [...new Set(axes.map((axis) => axis.category).filter(Boolean))],
    [axes]
  );

  const chartSeries = useMemo(
    () =>
      visiblePalettedItems
        .filter((item) => !item.disabled)
        .map((item) => {
          const ranges = rangesForRadarAxes(item.questionSets, axes, depth);
          return {
            key: item.key,
            name: item.name,
            inkColor: item.inkColor,
            ...seriesPaletteClasses(seriesPalette, item.paletteIndex),
            values: scoresForRadarAxes(item.questionSets, axes, depth),
            mins: ranges.map((range) => range.min),
            maxes: ranges.map((range) => range.max),
          };
        }),
    [visiblePalettedItems, axes, depth, seriesPalette]
  );

  const highlightedSeriesKey = useMemo(() => {
    const fallback = chartSeries[0]?.key ?? null;
    if (resolvedActive == null) {
      return fallback;
    }
    return chartSeries.some((item) => item.key === resolvedActive)
      ? resolvedActive
      : fallback;
  }, [chartSeries, resolvedActive]);

  const filledSeriesKey = useMemo(() => {
    if (resolvedActive == null) {
      return null;
    }
    return chartSeries.some((item) => item.key === resolvedActive)
      ? resolvedActive
      : null;
  }, [chartSeries, resolvedActive]);

  const lockedSeriesKey = useMemo(() => {
    if (resolvedPinned == null) {
      return null;
    }
    return chartSeries.some((item) => item.key === resolvedPinned)
      ? resolvedPinned
      : null;
  }, [chartSeries, resolvedPinned]);

  const disabledKeys = useMemo(
    () =>
      new Set(
        palettedItems.filter((item) => item.disabled).map((item) => item.key)
      ),
    [palettedItems]
  );

  const handleHoverSeries = useCallback(
    (key) => {
      if (disabledKeys.has(key)) {
        return;
      }
      clearTimeout(peekTimeout.current);
      peekTimeout.current = null;
      setInternalPeeked(key);
      onPeek(key);
    },
    [disabledKeys, onPeek]
  );

  const handleClearSeries = useCallback(() => {
    clearTimeout(peekTimeout.current);
    peekTimeout.current = setTimeout(() => {
      setInternalPeeked(null);
      onClearPeek();
    }, 100);
  }, [onClearPeek]);

  const handleClickSeries = useCallback(
    (key) => {
      if (disabledKeys.has(key)) {
        return;
      }
      if (resolvedPinned === key) {
        setInternalPinned(null);
        onClearPin();
      } else {
        setInternalPinned(key);
        onPin(key);
      }
    },
    [disabledKeys, resolvedPinned, onPin, onClearPin]
  );

  const showEmptyComponent =
    !isLoading && palettedItems.length === 0 && Boolean(EmptyComponent);
  const showEmptyMessage =
    !isLoading && palettedItems.length > 0 && chartSeries.length === 0;
  const showEmptyFrame = isLoading || showEmptyComponent || showEmptyMessage;
  const showLegend =
    !hideLegend &&
    !compact &&
    (palettedItems.length > 1 ||
      palettedItems.some((item) => item.disabled) ||
      (Boolean(EmptyComponent) && palettedItems.length === 0));
  const showDepthSelector = availableDepths.length > 1;
  const legendFields = useMemo(
    () =>
      palettedItems.map((item) => ({
        key: item.key,
        name: item.name,
        paletteIndex: item.paletteIndex,
        disabled: Boolean(item.disabled),
      })),
    [palettedItems]
  );

  const isLight = mode === 'light';

  const depthSelector = showDepthSelector ? (
    <div className={styles.depthSelector}>
      <label>View by</label>
      <Input
        type="select"
        bsSize="sm"
        className={classNames('limited-width-auto', { dark: !isLight })}
        value={depth}
        onChange={(event) => setDepth(event.target.value)}
        aria-label="Radar chart depth"
      >
        {availableDepths.map((availableDepth) => (
          <option key={availableDepth} value={availableDepth}>
            {radarDepthLabel(availableDepth)}
          </option>
        ))}
      </Input>
    </div>
  ) : null;

  const chart = (
    <div className={classNames(styles.chart, compact && styles.compactChart)}>
      {axes.length === 0 ? (
        showEmptyFrame ? (
          <div className={styles.emptyChartFrame} aria-hidden="true" />
        ) : (
          <div className="text-muted">No scored questions to plot.</div>
        )
      ) : (
        <RadarChart
          axes={axes}
          series={chartSeries}
          activeSeriesKey={
            chartSeries.length > 1 ? highlightedSeriesKey : chartSeries[0]?.key
          }
          filledSeriesKey={filledSeriesKey}
          lockedSeriesKey={lockedSeriesKey}
          dimInactive={dimInactive}
          onHoverSeries={handleHoverSeries}
          onClearSeries={handleClearSeries}
          onClickSeries={handleClickSeries}
        />
      )}
      {showEmptyComponent ? (
        <div className={styles.emptyContent} role="status">
          <EmptyComponent />
        </div>
      ) : showEmptyMessage ? (
        <div className={styles.emptyMessage} role="status">
          <span>{emptyMessage}</span>
        </div>
      ) : null}
      {isLoading ? (
        <div className={styles.chartLoading} aria-live="polite">
          <Icon icon={['fal', 'circle-notch']} spin />
          <span className="sr-only">Loading chart data</span>
        </div>
      ) : null}
    </div>
  );

  return (
    <>
      {compact ? null : (
        <div className="row">
          <div className="col">
            <CategoryPills
              categories={categories}
              categoryLookups={categoryLookups}
            />
          </div>
        </div>
      )}
      <div className={compact ? undefined : 'row'}>
        <div className={compact ? undefined : 'col'}>
          <div
            className={classNames(
              'assessment-history-chart',
              isLight ? 'series-radar-light chart-light' : 'series-radar-dark',
              styles.root,
              compact && styles.compactRoot,
              isLight ? styles.light : styles.dark
            )}
          >
            <div
              className={classNames(
                styles.layout,
                compact && styles.compactLayout
              )}
            >
              {compact ? (
                <>
                  {depthSelector}
                  {chart}
                </>
              ) : (
                <>
                  {chart}
                  <div className={styles.sidebar}>
                    {depthSelector}
                    {showLegend ? (
                      <SeriesRadarLegend
                        heading={legendHeading}
                        caption={legendCaption}
                        fields={legendFields}
                        seriesPalette={seriesPalette}
                        pinnedKey={resolvedPinned}
                        onPeek={handleHoverSeries}
                        onClearPeek={handleClearSeries}
                        onPin={handleClickSeries}
                        onClearPin={() => handleClickSeries(resolvedPinned)}
                        enableFilter={enableSeriesFilter}
                        maxSeries={maxSeries}
                        selectedKeys={selectedKeys}
                        onChangeSelectedKeys={setSelectedKeys}
                      />
                    ) : null}
                    {isLoading ? (
                      <div
                        className={styles.sidebarLoading}
                        aria-hidden="true"
                      />
                    ) : null}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default SeriesRadarChart;
